// src/pages/Achievements.tsx
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PageHero } from '@/components/PageHero';
import { achievements } from '@/data/mock/achievements';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Trophy, 
  Star, 
  Handshake, 
  Target,
  Award,
  Medal,
  Crown,
  TrendingUp,
  Calendar,
  Filter,
  Grid3x3,
  List,
  Sparkles,
  Zap,
  Flame,
  CheckCircle2,
  ArrowUpRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const Achievements = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  
  const [viewMode, setViewMode] = useState<'timeline' | 'grid'>('timeline');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedYear, setSelectedYear] = useState<string>('all');

  const iconMap: Record<string, any> = {
    Trophy, Star, Handshake, Target, Award, Medal, Crown
  };

  // Get unique years
  const years = ['all', ...Array.from(new Set(achievements.map(a => a.year.toString()))).sort((a, b) => Number(b) - Number(a))];

  // Categories with icons
  const categories = [
    { id: 'all', label: { en: 'All', ar: 'الكل' }, icon: Sparkles, color: 'primary' },
    { id: 'championship', label: { en: 'Titles', ar: 'ألقاب' }, icon: Trophy, color: 'yellow-500' },
    { id: 'partnership', label: { en: 'Partnerships', ar: 'شراكات' }, icon: Handshake, color: 'blue-500' },
    { id: 'milestone', label: { en: 'Milestones', ar: 'إنجازات' }, icon: Target, color: 'green-500' },
    { id: 'recognition', label: { en: 'Recognition', ar: 'تقدير' }, icon: Star, color: 'purple-500' },
  ];

  // Filter achievements
  const filteredAchievements = achievements
    .filter(a => selectedCategory === 'all' || a.category === selectedCategory)
    .filter(a => selectedYear === 'all' || a.year.toString() === selectedYear)
    .sort((a, b) => b.year - a.year);

  // Stats
  const stats = [
    { 
      icon: Trophy, 
      value: achievements.filter(a => a.category === 'championship').length.toString(),
      label: { en: 'Titles', ar: 'لقب' },
      color: 'from-yellow-500 to-orange-500'
    },
    { 
      icon: Handshake, 
      value: achievements.filter(a => a.category === 'partnership').length.toString(), 
      label: { en: 'Partnerships', ar: 'شراكة' },
      color: 'from-blue-500 to-cyan-500'
    },
    { 
      icon: Target, 
      value: achievements.filter(a => a.category === 'milestone').length.toString(), 
      label: { en: 'Milestones', ar: 'إنجاز' },
      color: 'from-green-500 to-emerald-500'
    },
    { 
      icon: Star, 
      value: achievements.filter(a => a.category === 'recognition').length.toString(), 
      label: { en: 'Awards', ar: 'جائزة' },
      color: 'from-purple-500 to-pink-500'
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Enhanced Page Hero */}
      <PageHero
        title={t('achievements.title')}
        subtitle={isRTL ? 'رحلة من الإنجازات والتميز المستمر' : 'A Journey of Continuous Achievement and Excellence'}
        breadcrumbs={[
          { label: t('common.home'), path: '/' },
          { label: t('nav.achievements') }
        ]}
      />

      {/* Stats Section */}
      <section className="py-16 bg-gradient-to-b from-background to-surface-1 relative overflow-hidden">
        {/* Background Decoration */}
        <div className="absolute inset-0 bg-grid-pattern opacity-5" />
        
        <div className="container mx-auto px-4 relative z-10">
          {/* Total Count Badge */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-gradient-to-r from-primary via-primary to-accent shadow-2xl shadow-primary/30 border border-primary/40">
              <Trophy className="h-8 w-8 text-primary-foreground" />
              <span className="text-4xl font-bold text-primary-foreground">{achievements.length}+</span>
              <span className="text-lg font-semibold text-primary-foreground">
                {isRTL ? 'إنجاز وتميز' : 'Achievements'}
              </span>
            </div>
          </motion.div>

          {/* Stats Grid */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-5xl mx-auto"
          >
            {stats.map((stat, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                whileHover={{ scale: 1.05, y: -8 }}
                className="relative p-8 rounded-3xl bg-card border border-border hover:border-primary/50 shadow-xl hover:shadow-2xl transition-all duration-500 text-center overflow-hidden group"
              >
                {/* Gradient Background */}
                <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} opacity-0 group-hover:opacity-10 transition-opacity duration-500`} />
                
                {/* Icon */}
                <div className="relative">
                  <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br ${stat.color} p-0.5 mb-4`}>
                    <div className="w-full h-full rounded-2xl bg-card flex items-center justify-center">
                      <stat.icon className="h-8 w-8 text-primary" />
                    </div>
                  </div>
                  
                  {/* Value */}
                  <div className="text-5xl font-bold gradient-text mb-2">
                    {stat.value}
                  </div>
                  
                  {/* Label */}
                  <div className="text-sm text-muted-foreground font-medium">
                    {stat.label[isRTL ? 'ar' : 'en']}
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Filters and View Toggle */}
      <section className="py-12 bg-surface-1 sticky top-0 z-40 backdrop-blur-xl bg-surface-1/80 border-b border-border">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            {/* Category Filters */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="flex flex-wrap gap-3 justify-center mb-6"
            >
              {categories.map((category) => {
                const Icon = category.icon;
                const isActive = selectedCategory === category.id;
                
                return (
                  <Button
                    key={category.id}
                    variant={isActive ? "default" : "outline"}
                    size="lg"
                    onClick={() => setSelectedCategory(category.id)}
                    className="rounded-full px-6 group"
                  >
                    <Icon className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                    {category.label[isRTL ? 'ar' : 'en']}
                    {category.id !== 'all' && (
                      <span className={`${isRTL ? 'mr-2' : 'ml-2'} px-2 py-0.5 rounded-full text-xs ${
                        isActive 
                          ? 'bg-primary-foreground/20' 
                          : 'bg-muted'
                      }`}>
                        {achievements.filter(a => a.category === category.id).length}
                      </span>
                    )}
                  </Button>
                );
              })}
            </motion.div>

            {/* Year Filter & View Toggle */}
            <div className="flex flex-wrap items-center justify-between gap-4">
              {/* Year Filter */}
              <div className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-muted-foreground" />
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                  className="px-4 py-2 rounded-full bg-card border border-border text-sm font-medium hover:border-primary/50 transition-colors cursor-pointer"
                >
                  {years.map(year => (
                    <option key={year} value={year}>
                      {year === 'all' 
                        ? (isRTL ? 'جميع السنوات' : 'All Years')
                        : year
                      }
                    </option>
                  ))}
                </select>
              </div>

              {/* View Toggle */}
              <div className="flex items-center gap-2 p-1 rounded-full bg-card border border-border">
                <Button
                  variant={viewMode === 'timeline' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('timeline')}
                  className="rounded-full"
                >
                  <List className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                  {isRTL ? 'خط زمني' : 'Timeline'}
                </Button>
                <Button
                  variant={viewMode === 'grid' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('grid')}
                  className="rounded-full"
                >
                  <Grid3x3 className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                  {isRTL ? 'شبكة' : 'Grid'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Results Count */}
      <section className="py-8 bg-surface-1">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto text-center">
            <p className="text-muted-foreground">
              {isRTL
                ? `تم العثور على ${filteredAchievements.length} ${selectedCategory === 'championship' ? 'لقب' : 'إنجاز'}`
                : `Found ${filteredAchievements.length} ${selectedCategory === 'championship' ? 'titles' : 'achievements'}`
              }
            </p>
          </div>
        </div>
      </section>

      {/* Achievements Display */}
      <AnimatePresence mode="wait">
        {viewMode === 'timeline' ? (
          // Timeline View
          <section key="timeline" className="py-20 bg-gradient-to-b from-surface-1 to-background">
            <div className="container mx-auto px-4">
              <div className="max-w-5xl mx-auto relative">
                {/* Timeline Line */}
                <div className={`absolute ${isRTL ? 'right-8 md:right-1/2' : 'left-8 md:left-1/2'} top-0 bottom-0 w-1 bg-gradient-to-b from-primary via-accent to-primary rounded-full`} />

                {/* Achievements */}
                {filteredAchievements.map((achievement, idx) => {
                  const Icon = iconMap[achievement.icon] || Trophy;
                  const isEven = idx % 2 === 0;

                  return (
                    <motion.div
                      key={achievement.id}
                      initial={{ opacity: 0, x: isEven ? -50 : 50 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true, margin: "-100px" }}
                      transition={{ duration: 0.6, delay: idx * 0.1 }}
                      className={`relative flex items-center mb-16 ${
                        isEven ? 'md:flex-row' : 'md:flex-row-reverse'
                      }`}
                    >
                      {/* Content Card */}
                      <div className={`flex-1 ${
                        isRTL 
                          ? (isEven ? 'md:text-left md:pl-20' : 'md:pr-20')
                          : (isEven ? 'md:text-right md:pr-20' : 'md:pl-20')
                      }`}>
                        <motion.div
                          whileHover={{ scale: 1.02, y: -8 }}
                          className="md:inline-block w-full md:w-auto bg-card border border-border rounded-3xl p-8 shadow-xl hover:shadow-2xl hover:border-primary/50 transition-all duration-500 overflow-hidden group"
                        >
                          {/* Card Background Glow */}
                          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                          <div className="relative">
                            {/* Header */}
                            <div className="flex items-center justify-between mb-4">
                              <div className="flex items-center gap-3">
                                <div className="p-3 rounded-2xl bg-primary/10 border border-primary/20">
                                  <Icon className="h-7 w-7 text-primary" />
                                </div>
                                <span className="text-3xl font-bold gradient-text">{achievement.year}</span>
                              </div>

                              {/* Category Badge */}
                              <div className="px-4 py-2 rounded-full bg-primary/10 border border-primary/20">
                                <span className="text-xs font-semibold text-primary uppercase">
                                  {achievement.category}
                                </span>
                              </div>
                            </div>

                            {/* Title */}
                            <h3 className="text-2xl font-bold mb-3 group-hover:text-primary transition-colors">
                              {achievement.title[isRTL ? 'ar' : 'en']}
                            </h3>

                            {/* Description */}
                            <p className="text-muted-foreground leading-relaxed">
                              {achievement.description[isRTL ? 'ar' : 'en']}
                            </p>

                            {/* Decorative Element */}
                            <div className="mt-6 flex items-center gap-2">
                              <CheckCircle2 className="h-5 w-5 text-green-500" />
                              <span className="text-sm font-medium text-green-500">
                                {isRTL ? 'إنجاز محقق' : 'Achieved'}
                              </span>
                            </div>
                          </div>
                        </motion.div>
                      </div>

                      {/* Timeline Node */}
                      <motion.div
                        whileHover={{ scale: 1.3, rotate: 360 }}
                        transition={{ duration: 0.6 }}
                        className={`absolute ${isRTL ? 'right-8 md:right-1/2' : 'left-8 md:left-1/2'} w-8 h-8 bg-gradient-to-br from-primary to-accent rounded-full border-4 border-background shadow-lg shadow-primary/50 transform -translate-x-1/2 z-10 flex items-center justify-center`}
                      >
                        <Sparkles className="h-4 w-4 text-primary-foreground" />
                      </motion.div>
                    </motion.div>
                  );
                })}

                {/* Timeline End */}
                <motion.div
                  initial={{ opacity: 0, scale: 0 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  className={`absolute ${isRTL ? 'right-8 md:right-1/2' : 'left-8 md:left-1/2'} bottom-0 transform -translate-x-1/2`}
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-primary to-accent rounded-full border-4 border-background shadow-2xl shadow-primary/50 flex items-center justify-center">
                    <Flame className="h-6 w-6 text-primary-foreground" />
                  </div>
                </motion.div>
              </div>
            </div>
          </section>
        ) : (
          // Grid View
          <section key="grid" className="py-20 bg-gradient-to-b from-surface-1 to-background">
            <div className="container mx-auto px-4">
              <div className="max-w-7xl mx-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {filteredAchievements.map((achievement, idx) => {
                    const Icon = iconMap[achievement.icon] || Trophy;
                    const category = categories.find(c => c.id === achievement.category);

                    return (
                      <motion.div
                        key={achievement.id}
                        initial={{ opacity: 0, y: 40 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-50px" }}
                        transition={{ duration: 0.6, delay: idx * 0.05 }}
                        whileHover={{ y: -12 }}
                        className="group"
                      >
                        <div className="relative h-full p-8 rounded-3xl bg-card border border-border hover:border-primary/50 shadow-xl hover:shadow-2xl transition-all duration-500 overflow-hidden">
                          {/* Background Glow */}
                          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                          <div className="relative">
                            {/* Icon & Year */}
                            <div className="flex items-start justify-between mb-6">
                              <div className="p-4 rounded-2xl bg-primary/10 border border-primary/20 group-hover:scale-110 transition-transform duration-300">
                                <Icon className="h-8 w-8 text-primary" />
                              </div>
                              <span className="text-4xl font-bold gradient-text">{achievement.year}</span>
                            </div>

                            {/* Category Badge */}
                            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-4">
                              {category && <category.icon className="h-4 w-4 text-primary" />}
                              <span className="text-xs font-semibold text-primary uppercase">
                                {achievement.category}
                              </span>
                            </div>

                            {/* Title */}
                            <h3 className="text-xl font-bold mb-3 group-hover:text-primary transition-colors leading-tight">
                              {achievement.title[isRTL ? 'ar' : 'en']}
                            </h3>

                            {/* Description */}
                            <p className="text-muted-foreground text-sm leading-relaxed mb-6">
                              {achievement.description[isRTL ? 'ar' : 'en']}
                            </p>

                            {/* Footer */}
                            <div className="flex items-center justify-between pt-6 border-t border-border">
                              <div className="flex items-center gap-2">
                                <CheckCircle2 className="h-5 w-5 text-green-500" />
                                <span className="text-sm font-medium text-green-500">
                                  {isRTL ? 'محقق' : 'Achieved'}
                                </span>
                              </div>
                              <ArrowUpRight className="h-5 w-5 text-primary opacity-0 group-hover:opacity-100 transition-opacity" />
                            </div>
                          </div>

                          {/* Shine Effect */}
                          <div
                            className={`absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent ${
                              isRTL
                                ? "translate-x-full group-hover:-translate-x-full"
                                : "-translate-x-full group-hover:translate-x-full"
                            } transition-transform duration-1000`}
                          />
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            </div>
          </section>
        )}
      </AnimatePresence>

      {/* No Results */}
      {filteredAchievements.length === 0 && (
        <section className="py-20 bg-background">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center max-w-md mx-auto"
            >
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-muted mb-6">
                <Filter className="h-10 w-10 text-muted-foreground" />
              </div>
              <h3 className="text-2xl font-bold mb-2">
                {isRTL ? 'لم يتم العثور على نتائج' : 'No Results Found'}
              </h3>
              <p className="text-muted-foreground mb-6">
                {isRTL 
                  ? 'جرب تغيير الفلاتر أو تصفح جميع الإنجازات'
                  : 'Try changing filters or browse all achievements'
                }
              </p>
              <Button
                onClick={() => {
                  setSelectedCategory('all');
                  setSelectedYear('all');
                }}
              >
                {isRTL ? 'إعادة تعيين الفلاتر' : 'Reset Filters'}
              </Button>
            </motion.div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-b from-background to-surface-1 relative overflow-hidden">
        {/* Animated Background */}
        <motion.div
          animate={{
            backgroundPosition: ["0% 50%", "100% 50%"],
          }}
          transition={{ duration: 20, repeat: Infinity, repeatType: "reverse" }}
          className="absolute inset-0 bg-gradient-to-r from-primary/5 via-accent/5 to-primary/5 bg-[length:200%_100%]"
        />

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary/10 border border-primary/20 mb-8">
              <Zap className="h-5 w-5 text-primary" />
              <span className="text-sm font-semibold text-primary">
                {isRTL ? 'انضم إلينا' : 'Join Us'}
              </span>
            </div>

            <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6 gradient-text">
              {isRTL ? 'كن جزءًا من النجاح' : 'Be Part of Success'}
            </h2>

            <p className="text-xl text-muted-foreground mb-10 max-w-2xl mx-auto">
              {isRTL
                ? 'انضم إلى أكاديمية تشامبيونز واصنع إنجازاتك الخاصة'
                : 'Join Champions Academy and create your own achievements'
              }
            </p>

            <Button size="lg" className="btn-glow px-12 py-7 text-lg rounded-full">
              <Trophy className={`h-5 w-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />
              {isRTL ? 'سجل الآن' : 'Register Now'}
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Achievements;
